import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 320,
        padding: '48px 24px',
        color: '#1C375D',
    },
    stage: {
        position: 'relative',
        width: 120,
        height: 120,
        marginBottom: 20,
    },
    grid: {
        position: 'absolute',
        inset: 18,
        borderRadius: 8,
        border: '2px dashed #c5d0de',
        background: 'repeating-linear-gradient(0deg, #f4f7fb, #f4f7fb 8px, #eef3f8 8px, #eef3f8 16px)',
        opacity: 0.9,
    },
    glass: {
        position: 'absolute',
        top: 8,
        left: 8,
        width: 56,
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#F47E46',
        filter: 'drop-shadow(0 2px 6px rgba(28, 55, 93, 0.25))',
        animation: '$scan 2.4s ease-in-out infinite',
    },
    glassIcon: {
        fontSize: 48,
    },
    percent: {
        fontSize: 28,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        marginBottom: 8,
    },
    label: {
        fontSize: 14,
        color: '#6b7a90',
        textAlign: 'center',
        maxWidth: 320,
        lineHeight: 1.45,
    },
    barTrack: {
        width: 'min(280px, 80vw)',
        height: 6,
        borderRadius: 999,
        backgroundColor: '#e7eef8',
        marginTop: 16,
        overflow: 'hidden',
    },
    barFill: {
        height: '100%',
        borderRadius: 999,
        background: 'linear-gradient(90deg, #1C375D, #F47E46)',
        transition: 'width 0.35s ease-out',
    },
    '@keyframes scan': {
        '0%': { transform: 'translate(0, 0) rotate(-8deg)' },
        '25%': { transform: 'translate(44px, 8px) rotate(6deg)' },
        '50%': { transform: 'translate(28px, 36px) rotate(-4deg)' },
        '75%': { transform: 'translate(8px, 20px) rotate(8deg)' },
        '100%': { transform: 'translate(0, 0) rotate(-8deg)' },
    },
}));

const PHASE_CONFIG = {
    sync: {
        floor: 5,
        cap: 38,
        step: [2, 5],
        message: 'Syncing latest campaign data from Meta…',
    },
    campaigns: {
        floor: 40,
        cap: 58,
        step: [3, 6],
        message: 'Loading campaign expenditure…',
    },
    daily: {
        floor: 62,
        cap: 96,
        step: [2, 5],
        message: 'Checking day-wise spend and leads for each city…',
    },
};

/**
 * Animated magnifying-glass loader with simulated progress until `active` becomes false.
 * @param {'sync'|'campaigns'|'daily'} phase
 */
export default function MetaAdsCampaignLoader({ active, phase = 'campaigns' }) {
    const classes = useStyles();
    const [progress, setProgress] = useState(0);
    const config = PHASE_CONFIG[phase] || PHASE_CONFIG.campaigns;

    useEffect(() => {
        if (!active) {
            setProgress(100);
            return undefined;
        }
        setProgress((prev) => (prev >= 100 ? config.floor : Math.max(prev, config.floor)));
        const tick = window.setInterval(() => {
            setProgress((prev) => {
                if (prev >= config.cap) return prev;
                const [minStep, maxStep] = config.step;
                const step = minStep + Math.random() * (maxStep - minStep);
                return Math.min(config.cap, prev + step);
            });
        }, 280);
        return () => window.clearInterval(tick);
    }, [active, phase, config.cap, config.floor, config.step]);

    useEffect(() => {
        if (!active && progress < 100) {
            setProgress(100);
        }
    }, [active, progress]);

    const message = config.message;

    return (
        <div className={classes.root} role="status" aria-live="polite" aria-busy={active}>
            <div className={classes.stage} aria-hidden>
                <div className={classes.grid} />
                <div className={classes.glass}>
                    <SearchIcon className={classes.glassIcon} />
                </div>
            </div>
            <div className={classes.percent}>
                {Math.round(progress)}
                %
            </div>
            <p className={classes.label}>{message}</p>
            <div className={classes.barTrack}>
                <div className={classes.barFill} style={{ width: `${Math.min(100, progress)}%` }} />
            </div>
        </div>
    );
}
